/**
 * Render a PDF's pages to images in the browser.
 *
 * The OpenAI Vision API only accepts images, so an Aadhaar PDF previously fell
 * back to the OCR.space text path — plain text OCR plus regex parsing, which is
 * markedly worse than the AI extraction images get. Rasterising the pages here
 * lets a PDF take exactly the same high-accuracy path as a photo.
 *
 * Uses the pdf.js LEGACY build: the modern build ships ESM that CRA 5's webpack
 * config does not transpile, and fails at build time.
 *
 * REQUIRES pdfjs-dist 3.x — the version is pinned exactly in package.json, no
 * caret. From v4 the legacy build ships only .mjs files and drops
 * pdf.worker.entry.js entirely, so the worker import below stops resolving and
 * `npm run build` dies with "Can't resolve 'pdfjs-dist/legacy/build/
 * pdf.worker.entry'". Do not bump this package without rewriting the worker
 * setup below.
 */

import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf";

// Imported for its SIDE EFFECT, not for a value: pdf.worker.entry assigns
// `window.pdfjsWorker = require("./pdf.worker.js")` and exports nothing.
//
// pdf.js checks `globalThis.pdfjsWorker?.WorkerMessageHandler` first
// (pdf.js:4537) and, when it finds it, runs the worker code on the main thread
// and never reads GlobalWorkerOptions.workerSrc at all. That is what makes this
// work under CRA 5 with no worker-loader, no ejecting, and no worker file to
// serve — and, unlike pointing workerSrc at a CDN, it keeps the app working
// with no outbound access to a third-party host.
//
// `import worker from ...` would bind undefined and setting workerSrc to that
// throws 'No "GlobalWorkerOptions.workerSrc" specified.' at the first render.
//
// Trade-off: parsing runs on the main thread, so the tab pauses briefly while
// a page rasterises. For a 1-2 page Aadhaar that is a blink; do not reuse this
// module for large documents without moving to a real worker.
import "pdfjs-dist/legacy/build/pdf.worker.entry";

/**
 * Thrown when the PDF needs a password.
 *
 * e-Aadhaar downloads from the UIDAI site are ALWAYS password protected (first
 * four letters of the name in caps + year of birth), so this is the common
 * case, not an edge case — the caller turns it into a message telling the user
 * what to do rather than a generic failure.
 */
export class PdfPasswordRequiredError extends Error {
  constructor(message) {
    super(message || "This PDF is password protected");
    this.name = "PdfPasswordRequiredError";
  }
}

/** Read a File into an ArrayBuffer */
const fileToArrayBuffer = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("Failed to read the PDF"));
    reader.readAsArrayBuffer(file);
  });

/**
 * Render the pages of a PDF to JPEG data URLs.
 *
 * @param {File} file        - the PDF
 * @param {object}  [options]
 * @param {string}  [options.password]  - password for a protected PDF
 * @param {number}  [options.maxPages]  - stop after this many pages (default 2:
 *   an Aadhaar is front + back, and every extra page costs another image in the
 *   Vision request)
 * @param {number}  [options.scale]     - render scale; 2 keeps the small
 *   address print legible, which is the whole point of doing this
 * @returns {Promise<string[]>} JPEG data URLs, one per rendered page
 */
export const renderPdfToImages = async (
  file,
  { password, maxPages = 2, scale = 2 } = {}
) => {
  const data = await fileToArrayBuffer(file);

  let pdf;
  try {
    pdf = await pdfjsLib.getDocument({
      data,
      ...(password ? { password } : {}),
      // The card is what matters; skipping these keeps rendering quick.
      disableAutoFetch: true,
      disableStream: true,
    }).promise;
  } catch (error) {
    // pdf.js reports both "needs a password" and "wrong password" as
    // PasswordException — either way the caller has to ask the user.
    if (
      error?.name === "PasswordException" ||
      /password/i.test(error?.message || "")
    ) {
      throw new PdfPasswordRequiredError(
        password
          ? "That password did not open the PDF"
          : "This PDF is password protected"
      );
    }
    throw new Error(`Could not read the PDF: ${error?.message || error}`);
  }

  const pageCount = Math.min(pdf.numPages, maxPages);
  const images = [];

  for (let pageNumber = 1; pageNumber <= pageCount; pageNumber++) {
    const page = await pdf.getPage(pageNumber);
    const viewport = page.getViewport({ scale });

    const canvas = document.createElement("canvas");
    canvas.width = Math.floor(viewport.width);
    canvas.height = Math.floor(viewport.height);
    const context = canvas.getContext("2d");

    // A transparent background renders as black in JPEG, which would hide the
    // text entirely — paint the page white first.
    context.fillStyle = "#FFFFFF";
    context.fillRect(0, 0, canvas.width, canvas.height);

    await page.render({ canvasContext: context, viewport }).promise;

    // JPEG at 0.92: the request carries the image as base64, and PNG at this
    // resolution is large enough to slow the upload noticeably.
    images.push(canvas.toDataURL("image/jpeg", 0.92));

    // Free the page's resources before rendering the next one.
    page.cleanup();
  }

  await pdf.destroy();

  if (!images.length) {
    throw new Error("The PDF contained no readable pages");
  }

  return images;
};

/** True when this file should go through the PDF path */
export const isPdfFile = (file) =>
  file?.type === "application/pdf" ||
  /\.pdf$/i.test(file?.name || "");

/** Turn a data URL back into a File so it can be stored/uploaded like any image */
const dataUrlToFile = (dataUrl, fileName) => {
  const [header, base64] = dataUrl.split(",");
  const mime = /:(.*?);/.exec(header)?.[1] || "image/jpeg";
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new File([bytes], fileName, { type: mime });
};

/**
 * Convert an uploaded PDF into a JPEG File.
 *
 * The Aadhaar front/back slots feed an <img> preview and the canvas merge that
 * builds the combined card image — neither can load a PDF. Converting once, at
 * upload, means every step after this point keeps working on an image and
 * needs no PDF special-casing at all.
 *
 * Only the FIRST page is used: these slots hold one side each.
 *
 * @param {File} file
 * @param {object} [options]
 * @param {string} [options.password]
 * @returns {Promise<File>} a JPEG File
 */
export const pdfFileToImageFile = async (file, { password } = {}) => {
  const [firstPage] = await renderPdfToImages(file, {
    password,
    maxPages: 1,
  });
  const name = (file.name || "document.pdf").replace(/\.pdf$/i, "") + ".jpg";
  return dataUrlToFile(firstPage, name);
};
